<template>
    <div class="grid flex flex-column justify-content-center align-items-center w-full">
        <CustomBreadcrumb class="flex align-self-start p-4 "
            :items="[{ label: 'Profile Settings' }, { label: 'Teaching' }]" />
        <span class="H6-Medium block text-center mt-6">Add details about yourself</span>
        <Form class=" md:col-11 md:p-5 mt-4 pl-4">
            <h4 class="Paragraph-Medium text-left">Select grade and subjects</h4>

            <div v-for="(level, index) in teachingLevels" :key="index" class="mb-4">
                <!-- Grade + Subjects row -->
                <div class="grid">
                    <div class="col-12 md:col-4 pr-3">
                        <div class="flex flex-column gap-2 pt-2 relative mb-5">
                            <label class="Label align-self-start">Grade :</label>
                            <Select
                                v-model="level.grade"
                                :options="gradeOptions"
                                :loading="isLoadingGrades"
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Select grade"
                                class="w-full"
                                size="small"
                                variant="filled"
                                show-clear
                                @change="onGradeChange(index)"
                                :optionDisabled="(option) => isGradeDisabled(option.code, index)"
                            />
                            <small v-if="errors[`grade_${index}`]" class="error-text">
                                {{ errors[`grade_${index}`] }}
                            </small>
                        </div>
                    </div>
                    <div class="col-12 md:col-8 pr-3">
                        <div class="flex flex-column gap-2 pt-2 relative mb-5">
                            <div class="flex justify-content-between align-items-center">
                                <label class="Label">Subjects :</label>
                                <span
                                    class="remove-grade-btn"
                                    :style="{ visibility: teachingLevels.length > 1 ? 'visible' : 'hidden' }"
                                    @click="removeGrade(index)"
                                >Remove</span>
                            </div>
                            <MultiSelect
                                v-model="level.subjects"
                                :options="getAvailableSubjects(level.grade)"
                                :disabled="!level.grade || getAvailableSubjects(level.grade).length === 0"
                                :optionDisabled="(option) => isSubjectDisabled(option, index)"
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Select subjects"
                                class="w-full"
                                size="small"
                                variant="filled"
                                show-clear
                                display="chip"
                                :maxSelectedLabels="3"
                                @change="() => onSubjectsChange(index)"
                            />
                            <small
                                :class="errors[`subjects_${index}`] ? 'error-text' : 'warning-text'"
                                :style="{ visibility: errors[`subjects_${index}`] || warnings[`subjects_${index}`] ? 'visible' : 'hidden' }"
                            >{{ errors[`subjects_${index}`] || warnings[`subjects_${index}`] || '' }}</small>
                        </div>
                    </div>
                </div>

                <!-- Per-subject class pricing -->
                <div v-if="level.subjects && level.subjects.length > 0" class="pricing-section ml-1 mr-1 mt-1 mb-2">
                    <!-- Column headers (desktop only) -->
                    <div class="grid align-items-center pricing-header py-1 desktop-header">
                        <div class="col-3">
                            <span class="pricing-col-label">Subject</span>
                        </div>
                        <div class="col-4 pl-2">
                            <span class="pricing-col-label">Individual Class / Hourly Rate</span>
                        </div>
                        <div class="col-4 pl-2">
                            <span class="pricing-col-label">Group Class / Hourly Rate</span>
                        </div>
                        <div class="col-1"></div>
                    </div>

                    <template v-for="subjectCode in level.subjects" :key="subjectCode">
                        <div class="grid align-items-center subject-pricing-row py-2">
                            <!-- Subject name (mobile: col-10, desktop: col-3) -->
                            <div class="col-10 md:col-3 flex align-items-center">
                                <span class="subject-label">{{ getSubjectName(level.grade, subjectCode) }}</span>
                            </div>

                            <!-- Remove button (mobile only) -->
                            <div class="col-2 flex justify-content-end align-items-center mobile-remove">
                                <button
                                    type="button"
                                    class="remove-subject-btn"
                                    title="Remove subject"
                                    @click="removeSubject(index, subjectCode)"
                                ><i class="pi pi-times"></i></button>
                            </div>

                            <!-- Individual class -->
                            <div class="col-12 md:col-4 pl-2">
                                <span class="mobile-col-label">Individual Class / Session</span>
                                <div class="flex align-items-center gap-1 w-full">
                                    <Checkbox
                                        v-model="level.subjectPricing[subjectCode].individualClass.available"
                                        :inputId="`ind_${index}_${subjectCode}`"
                                        binary
                                        class="teal-checkbox flex-shrink-0"
                                        @change="onClassTypeChange(index, subjectCode)"
                                    />
                                    <span class="rs-label flex-shrink-0">LKR.</span>
                                    <InputNumber
                                        v-model="level.subjectPricing[subjectCode].individualClass.charge"
                                        :disabled="!level.subjectPricing[subjectCode].individualClass.available"
                                        mode="decimal"
                                        :minFractionDigits="2"
                                        :maxFractionDigits="2"
                                        :useGrouping="true"
                                        size="small"
                                        variant="filled"
                                        class="flex-1"
                                        style="min-width:0"
                                        inputClass="w-full"
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            <!-- Group class -->
                            <div class="col-12 md:col-4 pl-2">
                                <span class="mobile-col-label">Group Class / Session</span>
                                <div class="flex align-items-center gap-1 w-full">
                                    <Checkbox
                                        v-model="level.subjectPricing[subjectCode].groupClass.available"
                                        :inputId="`grp_${index}_${subjectCode}`"
                                        binary
                                        class="teal-checkbox flex-shrink-0"
                                        @change="onClassTypeChange(index, subjectCode)"
                                    />
                                    <span class="rs-label flex-shrink-0">LKR.</span>
                                    <InputNumber
                                        v-model="level.subjectPricing[subjectCode].groupClass.charge"
                                        :disabled="!level.subjectPricing[subjectCode].groupClass.available"
                                        mode="decimal"
                                        :minFractionDigits="2"
                                        :maxFractionDigits="2"
                                        :useGrouping="true"
                                        size="small"
                                        variant="filled"
                                        class="flex-1"
                                        style="min-width:0"
                                        inputClass="w-full"
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            <!-- Remove subject (desktop only) -->
                            <div class="md:col-1 flex justify-content-end align-items-center desktop-remove">
                                <button
                                    type="button"
                                    class="remove-subject-btn"
                                    title="Remove subject"
                                    @click="removeSubject(index, subjectCode)"
                                ><i class="pi pi-times"></i></button>
                            </div>

                            <!-- Row-level message -->
                            <div class="col-12 md:col-9 md:col-offset-3 subject-msg-row">
                                <small
                                    :class="getSubjectMessage(index, subjectCode)?.type === 'error' ? 'msg-error' : 'msg-warning'"
                                    :style="{ visibility: getSubjectMessage(index, subjectCode) ? 'visible' : 'hidden' }"
                                >{{ getSubjectMessage(index, subjectCode)?.text ?? '' }}</small>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <div class="flex justify-content-start mb-4 pt-2">
                <Button label="Add a grade" class="teal-button mt-2" @click="addGrade" />
            </div>

            <Divider class="w-full" />

            <div class="grid w-full">
                <div class="col-12 md:col-6">
                    <div class="flex flex-column justify-content-start gap-3 pt-4">
                        <label for="occupation" class="Label align-self-start">I'm a : </label>
                        <Select v-model="selectedoccupation" :options="occupation" variant="filled" size="small"
                            optionLabel="value" placeholder="Select occupation type" class="w-full" show-clear />
                    </div>
                </div>
            </div>
            <div class="grid w-full">
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-3 pt-4">
                        <label for="subjects" class="Label align-self-start">I do classes:</label>
                        <Select v-model="selectedClassType" :options="classType" variant="filled" size="small"
                            optionValue="key" optionLabel="value" placeholder="Select class type" class="w-full"
                            show-clear default-value="online" />
                    </div>
                </div>
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-3 pt-4 justify-content-center align-items-center lg:px-8">
                        <label for="locations" class="Label align-self-start">Class location: (Not applicable for online classes)</label>
                        <RadioButtonGroup v-model="selectedLocation" name="location" class="flex flex-column gap-2">
                            <div v-for="item in locations" :key="item.key" class="flex items-center gap-2">
                                <RadioButton size="small" :inputId="item.key" :value="item.key"
                                    class="teal-radio-button" disabled />
                                <label class="Paragraph-Regular" :for="item.key">{{ item.value }}</label>
                            </div>
                        </RadioButtonGroup>
                    </div>
                </div>
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-3 pt-4">
                        <label for="occupation" class="Label align-self-start">My teaching language/s:</label>
                        <div class="flex justify-content-center">
                            <CheckboxGroup v-model="selectedLanguage" name="item" class="flex flex-column gap-2">
                                <div v-for="item in languages" :key="item.key" class="flex items-center gap-2">
                                    <Checkbox :inputId="item.key" :value="item.key" class="teal-checkbox" />
                                    <label class="Paragraph-Regular" :for="item.key">{{ item.value }}</label>
                                </div>
                            </CheckboxGroup>
                        </div>
                    </div>
                    <div class="flex flex-column gap-3 pt-4">
                        <label class="Label align-self-start">I'm available in:</label>
                        <div class="flex justify-content-center">
                            <CheckboxGroup v-model="selectedAvailability" name="availability"
                                class="flex flex-column gap-2">
                                <div v-for="item in availabilityOptions" :key="item.key"
                                    class="flex items-center gap-2">
                                    <Checkbox :inputId="item.key" :value="item.key" class="teal-checkbox" />
                                    <label class="Paragraph-Regular" :for="item.key">{{ item.value }}</label>
                                </div>
                            </CheckboxGroup>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12 col-6 flex justify-content-between">
                <div class="align-items-center gap-3">
                    <Button label="Save" class="teal-button mt-6" @click="Next"
                        :disabled="!isFormValid || !isFormDirty" />
                </div>
            </div>
        </Form>
    </div>
</template>

<script setup>
import { ref, defineEmits, defineProps, watch, computed, onMounted } from "vue";
import { Form } from "@primevue/forms";
import Button from "primevue/button";
import MultiSelect from 'primevue/multiselect';
import RadioButton from 'primevue/radiobutton';
import RadioButtonGroup from 'primevue/radiobuttongroup';
import Checkbox from 'primevue/checkbox';
import CheckboxGroup from 'primevue/checkboxgroup';
import InputNumber from 'primevue/inputnumber';
import Divider from 'primevue/divider';
import { Select } from "primevue";
import CustomBreadcrumb from '@/components/CustomBreadcrumb.vue';
import { occupation, classType, locations, languages, availabilityOptions, gradeOptions, isLoadingGrades, fetchGradeOptions } from "@/utils/options";
import { getLimits } from "@/api/tutor";

const emit = defineEmits(["submit-teaching-info", "previous-step", "update-profile"]);
const props = defineProps({
    profile: {
        type: Object,
        required: true
    }
});

const initSubjectPricing = () => ({
    individualClass: { available: false, charge: null },
    groupClass: { available: false, charge: null }
});

const teachingLevels = ref([
    { grade: null, subjects: [], subjectPricing: {} }
]);
const selectedoccupation = ref();
const selectedClassType = ref("online");
const selectedLocation = ref(null);
const selectedLanguage = ref([]);
const selectedAvailability = ref([]);

const originalState = ref(null);
const subjectLimit = ref(null);
const minSessionCharge = ref(30);
const errors = ref({});
const warnings = ref({});


onMounted(async () => {
    const [, limitsRes] = await Promise.allSettled([
        fetchGradeOptions(),
        getLimits(),
    ]);
    if (limitsRes.status === 'fulfilled') {
        const limits = limitsRes.value?.data?.data?.limits
        subjectLimit.value = limits?.listedSubjects ?? null;
        minSessionCharge.value = limits?.minSessionCharge ?? 30;
    }
});

const getAvailableSubjects = (gradeCode) => {
    if (!gradeCode) return [];
    const gradeObj = gradeOptions.value.find(g => g.code === gradeCode);
    return gradeObj && gradeObj.subjects ? gradeObj.subjects : [];
};

const getSubjectName = (gradeCode, subjectCode) => {
    const sub = getAvailableSubjects(gradeCode).find(s => s.code === subjectCode);
    return sub ? sub.name : subjectCode;
};

const onGradeChange = (index) => {
    teachingLevels.value[index].subjects = [];
    teachingLevels.value[index].subjectPricing = {};
    errors.value[`grade_${index}`] = null;
    warnings.value[`subjects_${index}`] = null;
};

const isGradeDisabled = (gradeCode, currentIndex) =>
    teachingLevels.value.some((l, i) => i !== currentIndex && l.grade === gradeCode);

const isSubjectDisabled = (option, levelIndex) => {
    if (!subjectLimit.value || subjectLimit.value.max === null) return false;
    const level = teachingLevels.value[levelIndex];
    return level.subjects.length >= subjectLimit.value.max && !level.subjects.includes(option.code);
};

const onSubjectsChange = (index) => {
    errors.value[`subjects_${index}`] = null;
    warnings.value[`subjects_${index}`] = null;

    const level = teachingLevels.value[index];

    level.subjects.forEach(code => {
        if (!level.subjectPricing[code]) level.subjectPricing[code] = initSubjectPricing();
    });
    Object.keys(level.subjectPricing).forEach(code => {
        if (!level.subjects.includes(code)) delete level.subjectPricing[code];
    });

    if (!subjectLimit.value || subjectLimit.value.max === null) return;

    const max = subjectLimit.value.max;

    // Trim if over per-grade limit
    if (level.subjects.length > max) {
        level.subjects = level.subjects.slice(0, max);
        Object.keys(level.subjectPricing).forEach(code => {
            if (!level.subjects.includes(code)) delete level.subjectPricing[code];
        });
    }

    if (level.subjects.length >= max) {
        warnings.value[`subjects_${index}`] = `Subject listing limit reached (${level.subjects.length}/${max})`;
    }
};

const removeGrade = (index) => {
    teachingLevels.value.splice(index, 1);
    Object.keys(errors.value)
        .filter(k => k.startsWith('grade_') || k.startsWith('subjects_') ||
            k.startsWith('classFormat_') || k.startsWith('indFee_') || k.startsWith('grpFee_'))
        .forEach(k => delete errors.value[k]);
    Object.keys(warnings.value).forEach(k => delete warnings.value[k]);
};

const onClassTypeChange = (levelIndex, subjectCode) => {
    delete errors.value[`classFormat_${levelIndex}_${subjectCode}`];
    const pricing = teachingLevels.value[levelIndex].subjectPricing[subjectCode];
    if (!pricing.individualClass.available) pricing.individualClass.charge = null;
    if (!pricing.groupClass.available) pricing.groupClass.charge = null;
};

const removeSubject = (levelIndex, subjectCode) => {
    const level = teachingLevels.value[levelIndex];
    level.subjects = level.subjects.filter(s => s !== subjectCode);
    delete level.subjectPricing[subjectCode];
    delete errors.value[`classFormat_${levelIndex}_${subjectCode}`];
    delete errors.value[`indFee_${levelIndex}_${subjectCode}`];
    delete errors.value[`grpFee_${levelIndex}_${subjectCode}`];

    if (subjectLimit.value?.max !== null) {
        if (level.subjects.length < subjectLimit.value.max) {
            warnings.value[`subjects_${levelIndex}`] = null;
        }
    }
};

const getSubjectMessage = (levelIndex, subjectCode) => {
    const pricing = teachingLevels.value[levelIndex]?.subjectPricing?.[subjectCode];
    if (!pricing) return null;

    const classFormatError = errors.value[`classFormat_${levelIndex}_${subjectCode}`];
    const indFeeError = errors.value[`indFee_${levelIndex}_${subjectCode}`];
    const grpFeeError = errors.value[`grpFee_${levelIndex}_${subjectCode}`];
    if (classFormatError) return { text: classFormatError, type: 'error' };
    if (indFeeError) return { text: indFeeError, type: 'error' };
    if (grpFeeError) return { text: grpFeeError, type: 'error' };

    const hasIndividual = pricing.individualClass?.available;
    const hasGroup = pricing.groupClass?.available;
    if (!hasIndividual && !hasGroup) {
        return { text: "Select at least one class type and enter a price per session", type: 'warning' };
    }
    if (hasIndividual && !pricing.individualClass.charge) {
        return { text: "Enter a price per session for individual class", type: 'warning' };
    }
    if (hasIndividual && pricing.individualClass.charge < minSessionCharge.value) {
        return { text: `Minimum hourly rate is LKR. ${minSessionCharge.value} for individual class`, type: 'error' };
    }
    if (hasGroup && !pricing.groupClass.charge) {
        return { text: "Enter a price per session for group class", type: 'warning' };
    }
    if (hasGroup && pricing.groupClass.charge < minSessionCharge.value) {
        return { text: `Minimum hourly rate is LKR. ${minSessionCharge.value} for group class`, type: 'error' };
    }
    return null;
};

watch(
    () => props.profile,
    (newData) => {
        if (newData) {
            // Pre-fill from new flat format: group entries by grade
            if (newData.teachingLevels && newData.teachingLevels.length > 0) {
                const grouped = {};
                newData.teachingLevels.forEach(entry => {
                    if (!grouped[entry.grade]) {
                        grouped[entry.grade] = { grade: entry.grade, subjects: [], subjectPricing: {} };
                    }
                    grouped[entry.grade].subjects.push(entry.subject);
                    grouped[entry.grade].subjectPricing[entry.subject] = {
                        individualClass: entry.individualClass || { available: false, charge: null },
                        groupClass: entry.groupClass || { available: false, charge: null }
                    };
                });
                teachingLevels.value = Object.values(grouped);
            } else {
                teachingLevels.value = [{ grade: null, subjects: [], subjectPricing: {} }];
            }

            selectedoccupation.value = newData.tutorType ? occupation.find(o => o.key === newData.tutorType) : null;
            selectedClassType.value = newData.classMode || "online";
            selectedLocation.value = newData.classLocation || null;
            selectedLanguage.value = newData.teachingLanguage || [];
            selectedAvailability.value = newData.teachingAvailability || [];

            originalState.value = {
                teachingLevels: JSON.parse(JSON.stringify(teachingLevels.value)),
                selectedoccupation: selectedoccupation.value,
                selectedClassType: selectedClassType.value,
                selectedLocation: selectedLocation.value,
                selectedLanguage: [...selectedLanguage.value],
                selectedAvailability: [...selectedAvailability.value],
            };
        }
    },
    { immediate: true }
);

const addGrade = () => {
    teachingLevels.value.push({ grade: null, subjects: [], subjectPricing: {} });
};

const Next = async () => {
    // Guard: check per-grade subject limit
    if (subjectLimit.value && subjectLimit.value.max !== null) {
        const max = subjectLimit.value.max;
        let hasError = false;
        teachingLevels.value.forEach((level, index) => {
            if (level.subjects.length > max) {
                errors.value[`subjects_${index}`] = `Subject limit exceeded — max ${max} per grade on your current plan.`;
                hasError = true;
            }
        });
        if (hasError) return;
    }

    const flatTeachingLevels = [];
    teachingLevels.value.forEach(level => {
        level.subjects.forEach(subjectCode => {
            const pricing = level.subjectPricing[subjectCode] || {};
            flatTeachingLevels.push({
                grade: level.grade,
                subject: subjectCode,
                individualClass: pricing.individualClass || { available: false, charge: null },
                groupClass: pricing.groupClass || { available: false, charge: null }
            });
        });
    });

    const payload = {
        teachingLevels: flatTeachingLevels,
        tutorType: selectedoccupation.value.key,
        classMode: selectedClassType.value,
        classLocation: selectedLocation.value,
        teachingLanguage: selectedLanguage.value,
        teachingAvailability: selectedAvailability.value,
    };
    emit("update-profile", payload);
};

const isFormValid = computed(() => {
    const levelsValid = teachingLevels.value.length > 0 &&
        teachingLevels.value.every(level =>
            level.grade &&
            level.subjects.length > 0 &&
            level.subjects.every(subjectCode => {
                const pricing = level.subjectPricing[subjectCode];
                if (!pricing) return false;
                const hasIndividual = pricing.individualClass?.available;
                const hasGroup = pricing.groupClass?.available;
                if (!hasIndividual && !hasGroup) return false;
                if (hasIndividual && (!pricing.individualClass.charge || pricing.individualClass.charge < minSessionCharge.value)) return false;
                if (hasGroup && (!pricing.groupClass.charge || pricing.groupClass.charge < minSessionCharge.value)) return false;
                return true;
            })
        );

    return (
        levelsValid &&
        !!selectedoccupation.value &&
        !!selectedClassType.value &&
        !!selectedLocation.value &&
        selectedLanguage.value.length > 0 &&
        selectedAvailability.value.length > 0
    );
});

const isFormDirty = computed(() => {
    if (!originalState.value) return false;

    return (
        JSON.stringify(teachingLevels.value) !== JSON.stringify(originalState.value.teachingLevels) ||
        originalState.value.selectedoccupation?.key !== selectedoccupation.value?.key ||
        originalState.value.selectedClassType !== selectedClassType.value ||
        originalState.value.selectedLocation !== selectedLocation.value ||
        JSON.stringify(originalState.value.selectedLanguage) !== JSON.stringify(selectedLanguage.value) ||
        JSON.stringify(originalState.value.selectedAvailability) !== JSON.stringify(selectedAvailability.value)
    );
});
</script>

<style scoped>
@media (max-width: 768px) {
    .form {
        min-width: none;
    }
}

.error-text {
    color: #dc2626;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.25rem;
    left: 0;
}

.warning-text {
    color: #d97706;
    font-size: 0.85rem;
    position: absolute;
    top: 100%;
    margin-top: 0.25rem;
    left: 0;
}

.remove-grade-btn {
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    cursor: pointer;
    line-height: 1;
}

.remove-grade-btn:hover {
    color: #dc2626;
    text-decoration: underline;
}

.pricing-section {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    border-radius: 0 0.25rem 0.25rem 0;
    background: var(--p-surface-50, #f8fafc);
    border-radius: 0.375rem;
}

.pricing-header {
    border-bottom: 1px solid var(--p-surface-border, #e2e8f0);
    margin-bottom: 0.25rem;
}

.pricing-col-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--p-text-muted-color, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.subject-label {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--p-text-color, #1e293b);
}

.subject-pricing-row:not(:last-child) {
    border-bottom: 1px solid var(--p-surface-border, #f1f5f9);
}

.remove-subject-btn {
    background: none;
    border: none;
    color: #acacac;
    font-size: 0.75rem;
    line-height: 1;
    cursor: pointer;
    padding: 0.3rem;
    border-radius: 0.25rem;
    transition: color 0.15s, background 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.remove-subject-btn:hover {
    color: #dc2626;
    background: #fee2e2;
}

.subject-msg-row {
    height: 1rem;
    overflow: hidden;
    padding: 0;
    display: flex;
    align-items: center;
}

.msg-warning {
    color: #d97706;
    font-size: 0.72rem;
}

.msg-error {
    color: #dc2626;
    font-size: 0.72rem;
}

.rs-label {
    font-size: 0.8rem;
    color: var(--p-text-muted-color, #64748b);
}

.mobile-col-label {
    display: none;
}

@media (max-width: 767px) {
    .desktop-header {
        display: none !important;
    }

    .desktop-remove {
        display: none !important;
    }

    .mobile-col-label {
        display: block;
        font-size: 0.7rem;
        font-weight: 600;
        color: var(--p-text-muted-color, #64748b);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin-bottom: 0.2rem;
    }
}

@media (min-width: 768px) {
    .mobile-remove {
        display: none !important;
    }
}
</style>
